import {
  FIELD_TYPES,
  type FieldSpec,
  type FieldType,
  SYSTEM_KEYS,
  type SystemKey,
} from '@fundline/shared/fields/values';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

export interface FieldDefinition extends FieldSpec {
  id: string;
  key: string;
  label: string;
  position: number;
  archivedAt: string | null;
}

function asFieldType(value: string): FieldType {
  return (FIELD_TYPES as readonly string[]).includes(value) ? (value as FieldType) : 'text';
}

function asSystemKey(value: string | null): SystemKey | null {
  return value && (SYSTEM_KEYS as readonly string[]).includes(value) ? (value as SystemKey) : null;
}

function asOptions(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** All field definitions of the workspace (archived included), in display order. */
export async function fetchFieldDefinitions(workspaceId: string): Promise<FieldDefinition[]> {
  const { data, error } = await supabase()
    .from('field_definitions')
    .select('id, key, label, type, options, position, system_key, archived_at, created_at')
    .eq('workspace_id', workspaceId)
    .order('position')
    .order('created_at');
  if (error) throw new Error(`Couldn't load fields: ${error.message}`);
  return data.map((row) => ({
    id: row.id,
    key: row.key,
    label: row.label,
    type: asFieldType(row.type),
    options: asOptions(row.options),
    position: row.position,
    systemKey: asSystemKey(row.system_key),
    archivedAt: row.archived_at,
  }));
}

export function useFieldDefinitionsQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.fieldDefinitions(workspaceId),
    queryFn: () => fetchFieldDefinitions(workspaceId),
  });
}

type FieldPatch = Partial<{
  label: string;
  options: string[];
  position: number;
  archived_at: string | null;
}>;

/** Admin edits to field definitions (RLS, column grants and triggers enforce the rules). */
export function useFieldMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.fieldDefinitions(workspaceId) });

  const create = useMutation({
    mutationFn: async (input: {
      key: string;
      label: string;
      type: FieldType;
      options: string[];
      position: number;
    }) => {
      const { error } = await supabase()
        .from('field_definitions')
        .insert({ workspace_id: workspaceId, ...input });
      if (error) throw new Error(friendlyFieldError(error.message));
    },
    onSettled: invalidate,
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: FieldPatch }) => {
      const { data, error } = await supabase()
        .from('field_definitions')
        .update(input.patch)
        .eq('workspace_id', workspaceId)
        .eq('id', input.id)
        .select('id');
      if (error) throw new Error(friendlyFieldError(error.message));
      if (data.length === 0) throw new Error('You are not allowed to change this field.');
    },
    onSettled: invalidate,
  });

  /** Writes new positions for every field whose place changed. */
  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const fields = queryClient.getQueryData<FieldDefinition[]>(
        queryKeys.fieldDefinitions(workspaceId),
      );
      const current = new Map((fields ?? []).map((f) => [f.id, f.position]));
      for (const [index, id] of orderedIds.entries()) {
        if (current.get(id) === index + 1) continue;
        const { error } = await supabase()
          .from('field_definitions')
          .update({ position: index + 1 })
          .eq('workspace_id', workspaceId)
          .eq('id', id);
        if (error) throw new Error(friendlyFieldError(error.message));
      }
    },
    onSettled: invalidate,
  });

  return { create, update, reorder };
}

function friendlyFieldError(message: string): string {
  if (message.includes('field_definitions_label_idx'))
    return 'Another field already has this label.';
  return message;
}
